import { ListTypes } from '$shared/components';
import { EntityParameterType } from '$shared/constants/EntityParameterType';
import { NoLabelKey } from '$shared/constants/global';
import { getFromObject, parseDynamicData } from '$shared/helpers/utils';
import DateString from '$shared/models/DateString';
import { DatasetMatchRecord } from '../DatasetMatchRecord';

const ResultKey: ObjectType<string> = {
  deaNumber: 'registrationNumber',
  name: 'name',
  additionalCompanyInfo: 'additionalCompanyInfo',
  drugSchedule: 'drugSchedules',
  degree: 'degree',
  expirationDate: 'expirationDate',
  activity: 'status',
  paymentIndicator: 'paymentIndicator',
  address: 'address',
  businessActivity: 'businessActivity',
  businessActivityCode: 'businessActivityCode',
  businessActivitySubCode: 'businessActivitySubCode',
  stateLicenseNumber: 'stateLicenseNumber',
  stateControlledSubstanceLicenseNumber: 'stateControlledSubstanceLicenseNumber'
};

const DeaxResultKey: ObjectType<string> = {
  '%DEA-X Number%': 'deax.number',
  certifiedPatientLimit: 'certifiedPatientLimit'
};

export class DeaDatasetMatchRecord extends DatasetMatchRecord {
  public get businessActivity(): string {
    return this.record?.businessActivity || '';
  }

  public get drugSchedule(): string {
    return this.record?.drugSchedules || '';
  }

  public get expirationDate(): string {
    return DateString.create(this.record?.expirationDate)?.getParsedDate() || '';
  }

  public get address(): string {
    return (
      [
        this.record?.address1,
        this.record?.address2,
        this.record?.city,
        this.record?.state,
        this.record.zipCode
      ]
        .filter(Boolean)
        .join(', ') || ''
    );
  }

  public get status(): string {
    return this.record?.active ? 'Active' : 'Not Active';
  }

  public get state(): string {
    return this.record?.state || '';
  }

  public get deax(): any {
    return this.record?.deax;
  }

  public get hasDeax(): boolean {
    return !!this.record?.deax;
  }

  public get deaxLabel(): string {
    return this.hasDeax ? `DEA-X - ${this.deax?.number}` : '';
  }

  public get degree(): string {
    return this.record?.degree || '';
  }

  public get stateLicenseNumber(): string {
    return this.record?.stateLicenseNumber || '';
  }

  public get stateControlledSubstanceLicenseNumber(): string {
    return this.record?.stateControlledSubstanceLicenseNumber || '';
  }

  public toList(): IDataList {
    const tempResult: IDataList = {};

    Object.entries(ResultKey).forEach(([key, value]: [string, any]) => {
      if (value === ResultKey.address) {
        tempResult[key] = this.address;
        return;
      }
      if (value === ResultKey.activity) {
        tempResult[key] = this.status;
        return;
      }
      if (value === ResultKey.name) {
        tempResult[key] = this.getName();
        return;
      }
      const parsedValue = getFromObject(value, this.record);
      if (!parsedValue) {
        return;
      }
      tempResult[key] = parseDynamicData(parsedValue);
    });

    if (this.hasDeax) {
      const tempdeaxDetails: IDataList = {};
      Object.entries(DeaxResultKey).forEach(([label, key]) => {
        tempdeaxDetails[label] = getFromObject(key, this.deax);
      });
      tempResult[`${NoLabelKey}deax`] = {
        component: ListTypes.DeaXListType,
        props: { value: tempdeaxDetails }
      };
    }

    return tempResult;
  }

  protected getResultKeyByType(type: EntityParameterType): string | undefined {
    if (type === EntityParameterType.FacilityName) {
      return 'name';
    }
    if (type === EntityParameterType.ProviderDea) {
      return 'deaNumber';
    }
  }
}
